import { useMemo, useState } from 'react';
import { horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { MessagesHooks } from '@frontend/api-messaging';
import { SMSDataSharedUtils } from '@frontend/api-sms-data';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InboxTemplatesModals, TemplatesPopover, useInboxTemplateFlows } from '@frontend/inbox-templates';
import { CHAT_HEIGHTS, HEADER_HEIGHT, MiniChatProps, useMiniChatStore, WIDTHS } from '@frontend/mini-chat-store';
import { ThreadSendingAreaComponents, ThreadSendingAreaHooks } from '@frontend/thread-sending-area';
import { theme } from '@frontend/theme';
import { Button, EmojiButton, useTooltip } from '@frontend/design-system';
import { MiniChatBody } from './mini-chat-body';
import { MiniChatHeader } from './mini-chat-header';

export const MiniChat = ({
  chatId,
  groupId,
  contextPersonId,
  personPhone,
  onSelectPersonPhone,
  onSelectLocationPhone,
  unreadCount,
  sizeVariant,
  onMaximize,
  onMinimize,
  onClose,
  threadId,
  targetSmsData,
  canSchedule,
  locationPhone,
  dragIsActive,
  isMobile,
  ...rest
}: MiniChatProps) => {
  const { updateChat } = useMiniChatStore();
  const [placeholderText, setPlaceHolderText] = useState<string | undefined>(undefined);
  const { t } = useTranslation('inbox-mini-chat');

  const { getThreadSMSItems } = SMSDataSharedUtils.threadItemsUtils;

  // Get thread messages for smart draft
  const { threadItems } = MessagesHooks.useThread({
    threadId: threadId ?? '',
    groupId,
    providedPersonPhone: personPhone,
  });

  // Filter messages for smart draft (last 5 messages with body)
  const smartDraftMessages = useMemo(() => {
    return getThreadSMSItems(threadItems)
      .filter((sms) => sms.body)
      .slice(-5);
  }, [threadItems]);

  const { smartDraft, showSmartDraftCard, closeSmartDraft, isLoadingSmartDraft } = ThreadSendingAreaHooks.useSmartDraft(
    {
      smartDraftMessages,
      threadId,
    }
  );

  const {
    tooltipProps: templateTooltipProps,
    triggerProps: templateTriggerProps,
    Tooltip: TemplateTooltip,
  } = useTooltip({ placement: 'top' });

  const { focusTextarea, insertText, setBody, templatePopoverState, ...threadSendingAreaState } =
    ThreadSendingAreaHooks.useThreadSendingArea({
      threadId,
      groupId,
      personPhone,
      contextPersonId: contextPersonId,
      uiType: 'mini-chat',
      locationPhone,
      sendSideEffects: {
        onSuccess: (res) => {
          if (res.threadId !== threadId || res.locationPhone !== locationPhone || res.locationId !== groupId) {
            updateChat(chatId, { threadId: res.threadId, locationPhone: res.locationPhone, groupId: res.locationId });
          }
        },
      },
    });
  const { availableTemplateFlows, modalsProps, person, isPrimaryContact, canChangeContextContact } =
    useInboxTemplateFlows({
      groupId,
      contextPersonId,
      personPhone,
      locationPhone: locationPhone ?? '',
      onRenderTemplate: ({ bodyValue }) => {
        setBody(bodyValue);
        focusTextarea();
      },
    });

  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useSortable({
    id: chatId,
    strategy: horizontalListSortingStrategy,
    disabled: dragIsActive,
  });

  const [isChangingContextPerson, setIsChangingContextPerson] = useState(false);

  return (
    <>
      <motion.div
        ref={setNodeRef}
        variants={{
          full: {
            height: CHAT_HEIGHTS['full'],
            width: WIDTHS['full'],
          },
          minimized: {
            height: CHAT_HEIGHTS['minimized'],
            width: WIDTHS['minimized'],
          },
          avatar: {
            height: CHAT_HEIGHTS['avatar'],
            width: WIDTHS['avatar'],
          },
        }}
        initial='avatar'
        animate={sizeVariant}
        exit={sizeVariant === 'full' ? 'minimized' : sizeVariant}
        css={{
          backgroundColor: theme.colors.white,
          boxShadow: theme.shadows.floating,
          borderTopLeftRadius: theme.borderRadius.medium,
          borderTopRightRadius: theme.borderRadius.medium,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        data-chatid={chatId}
        data-threadid={threadId}
        {...rest}
      >
        <div
          css={{
            height: '100%',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AnimatePresence>
            <MiniChatHeader
              key={'mini-chat-header' + chatId}
              chatId={chatId}
              groupId={groupId}
              personPhone={personPhone}
              onSelectPersonPhone={onSelectPersonPhone}
              locationPhone={locationPhone}
              onSelectLocationPhone={onSelectLocationPhone}
              contextPersonId={contextPersonId}
              unreadCount={unreadCount}
              sizeVariant={sizeVariant}
              onMaximize={onMaximize}
              onMinimize={onMinimize}
              onClose={onClose}
              hideButtons={dragIsActive}
              ref={setActivatorNodeRef}
              isMobile={isMobile}
              isSelectingContextContact={isChangingContextPerson}
              setIsSelectingContextContact={setIsChangingContextPerson}
              threadId={threadId}
              {...listeners}
              {...attributes}
            />
            {sizeVariant === 'full' && !dragIsActive ? (
              <MiniChatBody
                key={'mini-chat-body' + chatId}
                groupId={groupId}
                threadId={threadId}
                targetSmsData={targetSmsData}
                personPhone={personPhone}
                unreadCount={unreadCount}
                chatId={chatId}
              />
            ) : (
              <div
                key='mini-chat-body-placeholder'
                css={{
                  position: 'relative',
                  maxHeight: CHAT_HEIGHTS['full'] - HEADER_HEIGHT,
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              ></div>
            )}
            <ThreadSendingAreaComponents.ThreadSendingArea
              {...threadSendingAreaState}
              canSchedule={canSchedule}
              autoGrowTextareaProps={{
                maxRows: 3,
                minRows: 3,
              }}
              hideTypingIndicator={sizeVariant !== 'full'}
              buttonType='button'
              placeholderText={placeholderText || undefined}
              smartDraftCard={
                !threadSendingAreaState.canSendSMS && (
                  <ThreadSendingAreaComponents.SmartDraftCard
                    smartDraft={smartDraft}
                    show={showSmartDraftCard}
                    isLoading={isLoadingSmartDraft}
                    onMouseOver={() => setPlaceHolderText(smartDraft)}
                    onMouseLeave={() => setPlaceHolderText(undefined)}
                    onClick={() => {
                      focusTextarea();
                      setBody(smartDraft);
                      closeSmartDraft();
                      setPlaceHolderText(undefined);
                    }}
                    onClose={(e) => {
                      e.stopPropagation();
                      closeSmartDraft();
                      setPlaceHolderText(undefined);
                    }}
                  />
                )
              }
            >
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ThreadSendingAreaComponents.SignatureButton
                  size='large'
                  css={iconButtonStyles(threadSendingAreaState.disabled)}
                  groupId={groupId}
                  variant='secondary'
                  disabled={threadSendingAreaState.disabled}
                />
                <ThreadSendingAreaComponents.ImageButton
                  size='large'
                  css={iconButtonStyles(threadSendingAreaState.disabled)}
                  addImages={threadSendingAreaState.addMedia}
                  variant='secondary'
                  disabled={threadSendingAreaState.disabled}
                />
                <EmojiButton
                  // styles necessary to match this button to the sizing required by mini-chat designs
                  css={{
                    height: theme.spacing(4),
                    width: theme.spacing(4),
                    minWidth: 'unset',
                    padding: theme.spacing(1),
                  }}
                  onSelect={({ emoji, unified }) => {
                    if (unified !== '') {
                      insertText(emoji);
                    }
                  }}
                  disabled={threadSendingAreaState.disabled}
                />
                <div {...(!templatePopoverState.isOpen ? templateTriggerProps : {})}>
                  <Button
                    iconName='template'
                    variant='secondary'
                    size='large'
                    css={iconButtonStyles(threadSendingAreaState.disabled)}
                    {...templatePopoverState.getTriggerProps()}
                    disabled={threadSendingAreaState.disabled}
                    trackingId='mini-chat-templates-menu-button'
                  >
                    <Icon
                      name='caret-down-tiny'
                      size={8}
                      css={{
                        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: templatePopoverState.isOpen ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  </Button>
                  {!templatePopoverState.isOpen && (
                    <TemplateTooltip {...templateTooltipProps}>{t('Insert Template')}</TemplateTooltip>
                  )}
                </div>
              </div>
            </ThreadSendingAreaComponents.ThreadSendingArea>
          </AnimatePresence>
        </div>
      </motion.div>
      <InboxTemplatesModals {...modalsProps} />
      <TemplatesPopover
        getMenuProps={templatePopoverState.getMenuProps}
        getItemProps={templatePopoverState.getItemProps}
        templateItems={availableTemplateFlows}
        locationId={groupId}
        closePopover={templatePopoverState.close}
        person={person}
        isPrimaryContact={isPrimaryContact}
        onChangeContextPersonClick={
          canChangeContextContact
            ? () => {
                setIsChangingContextPerson(true);
              }
            : undefined
        }
      />
    </>
  );
};

const iconButtonStyles = (disabled?: boolean) =>
  css([
    {
      padding: theme.spacing(0.5),
      height: 'auto',
      ':hover': {
        backgroundColor: theme.colors.neutral5,
      },
      border: 'none',
    },
    disabled && {
      ':hover': {
        backgroundColor: 'transparent',
      },
      cursor: 'default',
      border: 'none',
    },
  ]);
